import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { X, Plus, Loader2 } from "lucide-react";

/**
 * Multi-tenant picker — chip list of selected client_ids + a dropdown
 * to add more. Used wherever an admin needs to scope to a SET of
 * tenants (currently: AdminsPage permissions tenancy editor).
 *
 * value:    string[] of selected client_ids
 * onChange: (next: string[]) => void
 *
 * Empty value array means "no scope yet".
 */
export default function MultiClientPicker({
    value,
    onChange,
    disabled = false,
    placeholder = "+ Add client…",
    testIdPrefix = "multi-client",
}) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/admin/clients`);
                setItems(data.items || []);
            } catch {
                /* ignore */
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const selected = useMemo(() => Array.isArray(value) ? value : [], [value]);
    const remaining = items.filter(
        (c) => !c.is_superdomain && !selected.includes(c.client_id)
    );

    const labelFor = (cid) => {
        const c = items.find((x) => x.client_id === cid);
        if (!c) return cid;
        return c.company_name || c.client_name || cid;
    };

    const add = (cid) => {
        if (!cid || selected.includes(cid)) return;
        onChange([...selected, cid]);
        setDraft("");
    };
    const remove = (cid) => onChange(selected.filter((c) => c !== cid));

    return (
        <div className="space-y-2" data-testid={`${testIdPrefix}-root`}>
            <div className="flex flex-wrap gap-1.5 min-h-[2.25rem] p-2 bg-[#050505] border border-white/10 rounded-md">
                {selected.length === 0 && (
                    <span className="text-[11px] text-white/30 font-mono py-1">
                        No tenants assigned yet.
                    </span>
                )}
                {selected.map((cid) => (
                    <span
                        key={cid}
                        data-testid={`${testIdPrefix}-chip-${cid}`}
                        className="inline-flex items-center gap-1.5 px-2 py-1 bg-white/5 border border-white/10 rounded-full text-xs"
                    >
                        <span className="font-mono text-white/85">{cid}</span>
                        <span className="text-white/40">·</span>
                        <span className="text-white/55">{labelFor(cid)}</span>
                        {!disabled && (
                            <button
                                type="button"
                                onClick={() => remove(cid)}
                                data-testid={`${testIdPrefix}-remove-${cid}`}
                                className="opacity-60 hover:opacity-100"
                                aria-label={`Remove ${cid}`}
                            >
                                <X className="w-3 h-3" />
                            </button>
                        )}
                    </span>
                ))}
            </div>
            {!disabled && (
                <div className="flex gap-2 items-center">
                    {loading
                        ? <span className="text-xs text-white/40 inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Loading clients…</span>
                        : (
                            <>
                                <Plus className="w-3.5 h-3.5 text-white/40" />
                                <select
                                    value={draft}
                                    onChange={(e) => add(e.target.value)}
                                    data-testid={`${testIdPrefix}-add`}
                                    className="bg-white/5 border border-white/10 rounded-md px-2 py-1.5 text-xs flex-1 max-w-md"
                                    disabled={remaining.length === 0}
                                >
                                    <option value="">
                                        {remaining.length === 0
                                            ? "All clients already added"
                                            : placeholder}
                                    </option>
                                    {remaining.map((c) => {
                                        const prefix = c.status !== "active" ? "(disabled) " : "";
                                        const label = `${prefix}${c.company_name || c.client_name} — ${c.client_id}`;
                                        return (
                                            <option key={c.client_id} value={c.client_id}>{label}</option>
                                        );
                                    })}
                                </select>
                            </>
                        )}
                </div>
            )}
        </div>
    );
}
